import React from 'react'
import PropTypes from 'prop-types'
import { Button, ButtonGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import FilterLeaf from './FilterLeaf'
import { addChild, canAddFilter, canAddGroup, newGroup, newLeaf, removeNode, updateNode } from './tree'

const OPERATORS = [
  { op: 'and', label: 'AND', hint: 'Match every filter in this group' },
  { op: 'or', label: 'OR', hint: 'Match at least one filter in this group' }
]

// A group of filters joined by AND or OR, holding filters and further groups.
const FilterGroup = ({ group, tree, setTree, isRoot = false, canEdit, ...leafProps }) => {
  const hint = OPERATORS.find(({ op }) => op === group.op)?.hint

  return (
    <div className={isRoot ? '' : 'border rounded p-2 mb-2'} data-testid="filter-group">
      <div className="d-flex align-items-center gap-2 mb-2">
        <ButtonGroup size="sm">
          {OPERATORS.map(({ op, label }) => (
            <Button
              key={op}
              variant={group.op === op ? 'primary' : 'light'}
              className={group.op === op ? '' : 'text-primary'}
              disabled={!canEdit}
              onClick={() => setTree((current) => updateNode(current, group.id, (node) => ({ ...node, op })))}
              data-testid={`filter-group-op-${op}`}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <span className="text-muted small">{hint}</span>
        {!isRoot && canEdit && (
          <Button
            variant="light"
            className="text-primary ms-auto"
            size="sm"
            title="Remove group"
            onClick={() => setTree((current) => removeNode(current, group.id))}
            data-testid="filter-group-remove"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </div>

      <div className={isRoot ? '' : 'ms-2'}>
        {group.children.map((child) =>
          Array.isArray(child.children) ? (
            <FilterGroup
              key={child.id}
              group={child}
              tree={tree}
              setTree={setTree}
              canEdit={canEdit}
              {...leafProps}
            />
          ) : (
            <FilterLeaf
              key={child.id}
              leaf={child}
              canEdit={canEdit}
              onChange={(fn) => setTree((current) => updateNode(current, child.id, fn))}
              onRemove={() => setTree((current) => removeNode(current, child.id))}
              {...leafProps}
            />
          )
        )}
        {group.children.length === 0 && <div className="text-muted small mb-2">No filters yet.</div>}
      </div>

      {canEdit && (
        <div className="d-flex gap-2">
          <Button
            variant="light"
            className="text-primary"
            size="sm"
            disabled={!canAddFilter(tree, group.id)}
            onClick={() => setTree((current) => addChild(current, group.id, newLeaf()))}
            data-testid="filter-group-add-filter"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Filter
          </Button>
          <Button
            variant="light"
            className="text-primary"
            size="sm"
            disabled={!canAddGroup(tree, group.id)}
            onClick={() => setTree((current) => addChild(current, group.id, newGroup(group.op === 'and' ? 'or' : 'and')))}
            data-testid="filter-group-add-group"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Group
          </Button>
        </div>
      )}
    </div>
  )
}

FilterGroup.propTypes = {
  group: PropTypes.object.isRequired,
  tree: PropTypes.object.isRequired,
  setTree: PropTypes.func.isRequired,
  isRoot: PropTypes.bool,
  canEdit: PropTypes.bool
}

export default FilterGroup
